"use client";

import React, { createContext, useCallback, useContext, useMemo } from "react";
import type { PromptCommandMode } from "./modelFormTypes";

interface PromptFieldContextValue {
  prompt: string;
  replacePrompt: (next: string) => void;
  appendPrompt: (next: string) => void;
  applyPromptCommand: (mode: PromptCommandMode, text: string) => void;
}

const PromptFieldContext = createContext<PromptFieldContextValue | null>(null);

interface PromptFieldProviderProps {
  prompt: string;
  onApplyCommand?: (mode: PromptCommandMode, text: string) => void;
  children: React.ReactNode;
}

const noop = () => {};

export const PromptFieldProvider: React.FC<PromptFieldProviderProps> = ({
  prompt,
  onApplyCommand = noop,
  children,
}) => {
  const applyCommand = useCallback(
    (mode: PromptCommandMode, text: string) => {
      onApplyCommand(mode, text);
    },
    [onApplyCommand]
  );

  const replacePrompt = useCallback(
    (next: string) => {
      applyCommand("replace", next);
    },
    [applyCommand]
  );

  const appendPrompt = useCallback(
    (next: string) => {
      applyCommand("append", next);
    },
    [applyCommand]
  );

  const value = useMemo(
    () => ({
      prompt,
      replacePrompt,
      appendPrompt,
      applyPromptCommand: applyCommand,
    }),
    [appendPrompt, applyCommand, prompt, replacePrompt]
  );

  return <PromptFieldContext.Provider value={value}>{children}</PromptFieldContext.Provider>;
};

export const usePromptField = (): PromptFieldContextValue => {
  const context = useContext(PromptFieldContext);
  if (!context) {
    throw new Error("usePromptField must be used within PromptFieldProvider");
  }
  return context;
};
